import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { WorkspaceStore } from '@shared/services/workspace.store';
import { readErrorTrail } from '@core/observability/error-trail';
import { SUPPORT_LIMITS, type SupportContext } from '@shared/definitions/support';

/**
 * Assembles the technical context attached to a support ticket.
 *
 * Everything here is shown to the user before it is sent — the form has a
 * "see what is sent" disclosure that renders this object verbatim, and a
 * checkbox that drops it entirely. That is the whole reason this is a service
 * returning a plain object rather than something the ticket callable derives
 * on its own: consent is only real if the thing consented to is legible.
 *
 * The build stamp is read from `/build-info.json`, which
 * `scripts/write-build-info.js` writes into `dist/vex/` at build time. It does
 * NOT exist under `ng serve` — a dev server has no build to stamp — so a miss
 * is normal and reported as `dev-server` rather than swallowed.
 */
@Injectable({ providedIn: 'root' })
export class SupportContextService {
  private readonly router = inject(Router);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly http = inject(HttpClient);

  /** Resolved once per session: the file cannot change under a loaded tab. */
  private buildVersion: string | null = null;

  /**
   * Build the context object for a new ticket.
   *
   * @param extra Fields the caller knows and this service cannot, e.g. the
   *   assistant conversation id when the ticket comes from the handoff (#291).
   * @returns The context to attach, never `null` — the caller decides whether
   *   to send it based on the consent checkbox.
   */
  async capture(extra?: Partial<SupportContext>): Promise<SupportContext> {
    const workspace = this.workspaceStore.currentWorkspace();
    // Two `Workspace` shapes coexist in this repo and disagree about
    // `subscription`: `@shared/definitions/workspace` declares `tier`, the
    // service-layer type the store actually emits declares only `status`.
    // Unifying them is frozen debt (CLAUDE.md — UI adapters only), so this
    // reads both defensively rather than picking a side and breaking the build.
    const subscription = workspace?.subscription as
      | { tier?: string; status?: string }
      | undefined;
    const tier = subscription?.tier ?? subscription?.status;
    return {
      route: this.currentRoute(),
      ...(workspace?.type ? { workspaceType: workspace.type } : {}),
      ...(tier ? { plan: tier } : {}),
      buildVersion: await this.resolveBuildVersion(),
      userAgent: navigator.userAgent,
      consoleErrors: readErrorTrail().slice(-SUPPORT_LIMITS.maxContextErrors),
      ...extra
    };
  }

  /**
   * The current route WITHOUT its query string.
   *
   * A query string is where ids, tokens and one-shot links live — the sign-in
   * link the tenant onboarding uses puts a credential there. Sending the path
   * says which screen broke; sending the query could hand us a secret the user
   * never meant to share.
   *
   * @returns The path portion of the current URL.
   */
  private currentRoute(): string {
    const url = this.router.url ?? '/';
    const cut = Math.min(
      ...[url.indexOf('?'), url.indexOf('#')].filter(i => i >= 0).concat([url.length])
    );
    return url.slice(0, cut);
  }

  /**
   * Read (once) the build stamp produced at deploy time.
   *
   * @returns A short git sha plus configuration, or `dev-server` when the file
   *   is absent — which is the normal state under `ng serve`.
   */
  private async resolveBuildVersion(): Promise<string> {
    if (this.buildVersion) return this.buildVersion;
    try {
      const info = await firstValueFrom(
        this.http.get<{ gitSha?: string; configuration?: string; dirty?: boolean }>('/build-info.json')
      );
      const sha = (info?.gitSha ?? 'unknown').slice(0, 9);
      const config = info?.configuration ?? 'unknown';
      this.buildVersion = `${config}@${sha}${info?.dirty ? '-dirty' : ''}`;
    } catch {
      this.buildVersion = 'dev-server';
    }
    return this.buildVersion;
  }
}
